import { useCallback } from 'react';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { performPostRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import type { CollaborationGraphEntities, Collaborators } from './types.tsx';

const COLLABORATION_GRAPH_MAX_RESULTS = 50;

export const useUsersFromCollaborationGraph = (cloudId: string) => {
	const atlassianAccountId = useAccountId();
	return useCallback(async (): Promise<Collaborators> => {
		try {
			const payload = {
				context: {
					contextType: 'crossJoinThirdPartyNudges',
					principalId: atlassianAccountId,
					siteId: cloudId,
				},
				modelRequestParams: {
					experience: 'CgUserNearbyUser',
				},
				expanded: true,
				maxNumberOfResults: COLLABORATION_GRAPH_MAX_RESULTS,
				userId: atlassianAccountId,
			};
			const options = {
				body: JSON.stringify(payload),
			};
			return performPostRequest<CollaborationGraphEntities>(
				'/gateway/api/collaboration/v1/collaborationgraph/user/user',
				options,
			).then((entities) =>
				entities.collaborationGraphEntities
					.filter((en) => en.userProfile !== undefined)
					.map((en) => ({
						id: en.userProfile.account_id,
						name: en.userProfile.name,
						avatar: en.userProfile.picture,
					})),
			);
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			fireErrorAnalytics({
				meta: {
					id: 'useUsersFromCollaborationGraph',
					packageName: 'jiraCrossJoinThirdPartyNudges',
				},
				error,
				sendToPrivacyUnsafeSplunk: true,
			});
			throw new Error('Get collaborators error');
		}
	}, [atlassianAccountId, cloudId]);
};
